// WebhookConfigDialog — Create / Edit a webhook config.
//
// - POST /api/v1/webhooks/configs            (create)
// - PUT  /api/v1/webhooks/configs/{id}       (edit)
//
// Webhook type is fixed (SMS delivery is the only type) and sent from code.
// Workspace, Secret and the Advanced expander (timeout/retry/headers/event
// filter) are staff-only: platform admin + support roles see them; tenants
// get the defaults. Secret is write-only: the API never returns it, so when
// editing we leave the field empty and only send a new secret if typed.
import React, {Component} from 'react';
import {ExpandMore, ExpandLess, Add as AddIcon, Close as CloseIcon} from '@mui/icons-material';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Select, MenuItem, FormControl, InputLabel, CircularProgress, IconButton,
  Chip, Typography
} from '@mui/material';

import Api from '../../api';
import {UI_API_URL_PATH_NEW} from '../../lib/constant';
import {getFromLocalStorage} from '../../lib/utils';
import {isAdminPanelRole} from '../../auth/roles';

const DEFAULTS = {
  workspaceId: 'sms',
  webhookType: 'WEBHOOK_TYPE_SMS_DELIVERY',
  method: 'WEBHOOK_METHOD_POST',
  contentType: 'application/json',
  timeoutMs: 5000,
  retryCount: 3,
  retryBaseMs: 333
};

// Staff = admin-panel roles (platform_admin/admin/admin_agent/support_agent).
// Deliberately NOT excluded during impersonation — support reaches a tenant's
// Developers Tool by impersonating, and these knobs are the reason they come.
const isStaff = () => isAdminPanelRole((getFromLocalStorage() || {}).role);

// Lightweight client-side URL sanity check. The real SSRF + DNS validation
// happens server-side; this just catches obvious typos before the network
// round-trip.
function quickUrlCheck(url) {
  if (!url) return 'URL is required';
  try {
    const u = new URL(url);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') {
      return 'Only http:// and https:// are supported';
    }
    const h = u.hostname.toLowerCase();
    if (h === 'localhost' || h.endsWith('.localhost') || h === '127.0.0.1' || h === '::1') {
      return 'Loopback hosts are not allowed';
    }
    // Catch 10.x/192.168/172.16-31/169.254 via IPv4 prefix match
    const v4 = h.match(/^(\d+)\.(\d+)\./);
    if (v4) {
      const a = +v4[1];
      const b = +v4[2];
      if (a === 10) return 'Private networks are not allowed';
      if (a === 192 && b === 168) return 'Private networks are not allowed';
      if (a === 172 && b >= 16 && b <= 31) return 'Private networks are not allowed';
      if (a === 169 && b === 254) return 'Link-local addresses are not allowed';
      if (a === 100 && b >= 64 && b <= 127) return 'CGNAT addresses are not allowed';
    }
  } catch (e) {
    return 'Malformed URL';
  }
  return null;
}

export default class WebhookConfigDialog extends Component {
  constructor(props) {
    super(props);
    const existing = props.config; // null on create, object on edit

    this.staff = isStaff();

    this.state = {
      name: existing?.name || '',
      description: existing?.description || '',
      workspaceId: existing?.workspaceId || DEFAULTS.workspaceId,
      webhookType: existing?.webhookType || DEFAULTS.webhookType,
      targetUrl: existing?.targetUrl || '',
      method: existing?.method || DEFAULTS.method,
      contentType: existing?.contentType || DEFAULTS.contentType,
      secret: '',                           // Always blank on open — write-only.
      timeoutMs: existing?.timeoutMs || DEFAULTS.timeoutMs,
      retryCount: existing?.retryCount ?? DEFAULTS.retryCount,
      retryBaseMs: existing?.retryBaseMs || DEFAULTS.retryBaseMs,
      headers: this.headersToPairs(existing?.headers),
      eventFilter: Array.isArray(existing?.eventFilter) ? existing.eventFilter : [],
      advancedOpen: false,
      submitting: false,
      urlError: null
    };
  }

  headersToPairs(headers) {
    if (!headers || typeof headers !== 'object') return [];
    return Object.entries(headers).map(([k, v]) => ({key: k, value: v}));
  }

  pairsToHeaders(pairs) {
    const out = {};
    for (const {key, value} of pairs) {
      const k = (key || '').trim();
      if (k) out[k] = value || '';
    }
    return out;
  }

  setHeader = (idx, field, val) => {
    const headers = [...this.state.headers];
    headers[idx] = {...headers[idx], [field]: val};
    this.setState({headers});
  }

  addHeader = () => this.setState({headers: [...this.state.headers, {key: '', value: ''}]})

  removeHeader = (idx) => {
    const headers = [...this.state.headers];
    headers.splice(idx, 1);
    this.setState({headers});
  }

  toggleEventFilter = (status) => {
    const cur = new Set(this.state.eventFilter);
    if (cur.has(status)) cur.delete(status); else cur.add(status);
    this.setState({eventFilter: Array.from(cur)});
  }

  validate = () => {
    const {name, workspaceId, targetUrl} = this.state;
    if (!name.trim()) return 'Name is required';
    if (!workspaceId.trim()) return 'Workspace is required';
    const urlErr = quickUrlCheck(targetUrl.trim());
    if (urlErr) { this.setState({urlError: urlErr}); return urlErr; }
    this.setState({urlError: null});
    return null;
  }

  submit = () => {
    const err = this.validate();
    if (err) {
      this.props.onError?.(err);
      return;
    }
    const {
      name, description, workspaceId, webhookType, targetUrl, method,
      contentType, secret, timeoutMs, retryCount, retryBaseMs, eventFilter
    } = this.state;

    const body = {
      name: name.trim(),
      description: description.trim(),
      workspace_id: workspaceId.trim(),
      webhook_type: webhookType,
      target_url: targetUrl.trim(),
      method,
      content_type: contentType,
      headers: this.pairsToHeaders(this.state.headers),
      timeout_ms: Number(timeoutMs) || DEFAULTS.timeoutMs,
      retry_count: Number(retryCount) || DEFAULTS.retryCount,
      retry_base_ms: Number(retryBaseMs) || DEFAULTS.retryBaseMs,
      event_filter: eventFilter
    };
    // Only include `secret` when the user typed one — an empty field on edit
    // means "leave the existing secret unchanged".
    if (secret) body.secret = secret;

    const isEdit = !!this.props.config?.id;
    const url = isEdit
      ? `${UI_API_URL_PATH_NEW}/api/v1/webhooks/configs/${this.props.config.id}`
      : `${UI_API_URL_PATH_NEW}/api/v1/webhooks/configs`;
    const req = isEdit ? Api.put(url, body) : Api.post(url, body);

    this.setState({submitting: true});
    req
      .then(() => {
        this.setState({submitting: false});
        this.props.onSaved?.(isEdit ? 'Webhook updated' : 'Webhook created');
      })
      .catch(e => {
        this.setState({submitting: false});
        const msg = e?.response?.data?.message || e?.message || 'Save failed';
        this.props.onError?.(msg);
      });
  }

  render() {
    const {
      name, workspaceId, targetUrl, method,
      contentType, secret, timeoutMs, retryCount, retryBaseMs, headers,
      eventFilter, advancedOpen, submitting, urlError
    } = this.state;
    const isEdit = !!this.props.config?.id;
    const isGet = method === 'WEBHOOK_METHOD_GET';
    const staff = this.staff;

    return (
      <Dialog open onClose={this.props.onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{isEdit ? 'Edit Webhook' : 'Create Webhook'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus margin="dense" label="Name *" fullWidth variant="outlined"
            value={name} onChange={(e) => this.setState({name: e.target.value})}
            placeholder="e.g. Delivery events"
            style={{marginBottom: 12}}
          />
          {staff && (
            <TextField
              margin="dense" label="Workspace *" fullWidth variant="outlined"
              value={workspaceId} onChange={(e) => this.setState({workspaceId: e.target.value})}
              placeholder="sms"
              helperText="Workspace id this webhook routes for. Leave as 'sms' if you only run one channel."
              style={{marginBottom: 12}}
              disabled={isEdit}
            />
          )}

          <TextField
            margin="dense" label="Target URL *" fullWidth variant="outlined"
            value={targetUrl}
            onChange={(e) => this.setState({targetUrl: e.target.value, urlError: null})}
            placeholder="https://hooks.example.com/dlr"
            error={!!urlError}
            helperText={urlError || 'http or https; public URLs only (no loopback / private IPs).'}
            style={{marginBottom: 12}}
          />
          <FormControl fullWidth style={{marginBottom: 12}}>
            <InputLabel>Method *</InputLabel>
            <Select value={method} label="Method *" onChange={(e) => this.setState({method: e.target.value})}>
              <MenuItem value="WEBHOOK_METHOD_POST">POST (JSON body)</MenuItem>
              <MenuItem value="WEBHOOK_METHOD_GET">GET (query string)</MenuItem>
            </Select>
          </FormControl>
          {!isGet && (
            <TextField
              margin="dense" label="Content-Type" fullWidth variant="outlined"
              value={contentType}
              onChange={(e) => this.setState({contentType: e.target.value})}
              style={{marginBottom: 12}}
            />
          )}

          {staff && (
            <TextField
              margin="dense" label={isEdit ? 'New Secret (leave blank to keep existing)' : 'Secret (HMAC, reserved for v2)'}
              fullWidth variant="outlined" type="password"
              value={secret} onChange={(e) => this.setState({secret: e.target.value})}
              helperText="Optional. Not validated by the dispatcher yet — HMAC signing lands in a later release."
              style={{marginBottom: 12}}
            />
          )}

          {staff && (
            <Button
              size="small"
              startIcon={advancedOpen ? <ExpandLess /> : <ExpandMore />}
              onClick={() => this.setState({advancedOpen: !advancedOpen})}
              style={{marginBottom: 8}}
            >
              Advanced
            </Button>
          )}

          {staff && advancedOpen && (
            <div style={{paddingLeft: 4, borderLeft: '2px solid #eee', paddingTop: 4, marginBottom: 8}}>
              <div style={{display: 'flex', gap: 8, marginBottom: 12}}>
                <TextField
                  label="Timeout (ms)" type="number" size="small" variant="outlined"
                  value={timeoutMs} onChange={(e) => this.setState({timeoutMs: e.target.value})}
                  style={{flex: 1}}
                />
                <TextField
                  label="Retries" type="number" size="small" variant="outlined"
                  value={retryCount} onChange={(e) => this.setState({retryCount: e.target.value})}
                  style={{flex: 1}}
                />
                <TextField
                  label="Retry base (ms)" type="number" size="small" variant="outlined"
                  value={retryBaseMs} onChange={(e) => this.setState({retryBaseMs: e.target.value})}
                  style={{flex: 1}}
                />
              </div>

              <Typography variant="subtitle2" style={{marginTop: 8}}>Headers</Typography>
              {headers.map((h, idx) => (
                <div key={idx} style={{display: 'flex', gap: 8, alignItems: 'center', marginTop: 6}}>
                  <TextField
                    label="Name" size="small" variant="outlined" style={{flex: 1}}
                    value={h.key} onChange={(e) => this.setHeader(idx, 'key', e.target.value)}
                  />
                  <TextField
                    label="Value" size="small" variant="outlined" style={{flex: 2}}
                    value={h.value} onChange={(e) => this.setHeader(idx, 'value', e.target.value)}
                  />
                  <IconButton size="small" onClick={() => this.removeHeader(idx)}><CloseIcon fontSize="small" /></IconButton>
                </div>
              ))}
              <Button size="small" startIcon={<AddIcon />} onClick={this.addHeader} style={{marginTop: 6}}>
                Add header
              </Button>

              <Typography variant="subtitle2" style={{marginTop: 16}}>
                Event filter (leave empty to receive all events)
              </Typography>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6}}>
                {['DELIVERED', 'FAILED', 'REJECTED', 'EXPIRED', 'UNDELIVERABLE'].map(s => {
                  const selected = eventFilter.includes(s);
                  return (
                    <Chip
                      key={s} label={s}
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={() => this.toggleEventFilter(s)}
                      size="small"
                    />
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={this.props.onClose} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={this.submit}
            disabled={submitting || !name.trim() || !targetUrl.trim() || !workspaceId.trim()}
          >
            {submitting ? <CircularProgress size={20} /> : (isEdit ? 'Save' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}
